import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info, Mic } from "lucide-react";
import episodes from "@/content/podcasty.json";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const formatDate = (date) =>
  new Date(date).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" });

export default function Podcasty() {
  useEffect(() => {
    document.title = "Podcast | Daniel Pekař";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Podcast o hypotékách, úvěrech a pojištění — srozumitelně, bez odborných pojmů. Poslechněte si na cestách nebo u kávy.");
    return () => { document.title = "Daniel Pekař – finanční poradce Ústí nad Labem"; };
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Podcast
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.12] text-[#252525] mt-6 max-w-2xl">
            O financech srozumitelně — i na cestách
          </h1>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-8 max-w-2xl">
            <span className="text-[#252525] font-medium">Průvodce finančním podcastem Daniela Pekaře</span> — hypotéky,
            úvěry a pojištění v audio podobě, jednoduše a bez složitých pojmů. Poslechněte si na cestách
            nebo u kávy, co se vyplatí vědět, než cokoli podepíšete.
          </p>
          <p className="text-xs uppercase tracking-[0.25em] text-[#B69A58] font-medium mt-6">
            Finance, kterým rozumíte
          </p>

          {episodes.length === 0 ? (
            <div className="mt-14 border border-[#F0F0F0] p-8 md:p-10 max-w-2xl" data-testid="podcast-empty">
              <div className="flex items-center gap-4">
                <Mic className="h-6 w-6 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
                <p className="text-base text-[#252525]">První epizody právě připravuji — brzy budou k poslechu.</p>
              </div>
            </div>
          ) : (
            <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-10">
              {episodes.map((episode, i) => (
                <article
                  key={episode.slug}
                  data-testid={`podcast-card-${episode.slug}`}
                  className="border border-[#F0F0F0] hover:border-[#B69A58] transition-colors duration-300 flex flex-col"
                >
                  {episode.image && (
                    <Link to={`/podcasty/${episode.slug}`} tabIndex={-1} aria-hidden="true">
                      <img
                        src={episode.image}
                        alt=""
                        width={840}
                        height={560}
                        loading="lazy"
                        className="w-full aspect-[3/2] object-cover object-[50%_30%] block"
                      />
                    </Link>
                  )}
                  <div className="p-8 md:p-10 flex flex-col flex-1">
                    <p className="text-xs uppercase tracking-widest text-[#B69A58] font-medium">
                      Epizoda {i + 1}
                      <span className="text-[#737373] font-normal">
                        {" · "}{formatDate(episode.date)}{episode.duration ? ` · ${episode.duration}` : ""}
                      </span>
                    </p>
                    <h2 className="font-display text-2xl md:text-3xl leading-snug text-[#252525] mt-4">
                      <Link
                        to={`/podcasty/${episode.slug}`}
                        data-testid={`podcast-link-${episode.slug}`}
                        className="hover:text-[#B69A58] transition-colors duration-300"
                      >
                        {episode.title}
                      </Link>
                    </h2>
                    <p className="text-base text-[#4A4A4A] leading-relaxed mt-4 flex-1">{episode.excerpt}</p>
                    {episode.aiGenerated && (
                      <p className="mt-4 flex items-center gap-2 text-xs text-[#737373]" data-testid={`podcast-ai-badge-${episode.slug}`}>
                        <Info className="h-3.5 w-3.5 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        Vytvořeno s využitím umělé inteligence
                      </p>
                    )}
                    <Link
                      to={`/podcasty/${episode.slug}`}
                      data-testid={`podcast-cta-${episode.slug}`}
                      className="group mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
                    >
                      Poslechnout epizodu
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
